import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Form, Spinner } from "react-bootstrap";
import { uploadFileInChat } from "../../reducers/commonReducer";
import { parseBannerSlides } from "../../helpers/bannerSlides";

const BannerSlideManager = ({ initialValue = "", initialLinkUrl = "", onChange }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [slides, setSlides] = useState(() =>
    parseBannerSlides(initialValue, initialLinkUrl)
  );
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setSlides(parseBannerSlides(initialValue, initialLinkUrl));
  }, [initialValue, initialLinkUrl]);

  const notify = (next) => onChange(JSON.stringify(next));

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setUploading(true);
    try {
      const resp = await dispatch(uploadFileInChat({ fileupload: [file] }));
      const url = resp?.payload?.uploads?.[0]?.cloudUrl || "";
      if (url) {
        setSlides((prev) => {
          const next = [...prev, { image: url, link: "" }];
          notify(next);
          return next;
        });
      }
    } finally {
      setUploading(false);
    }
  };

  const updateLink = (i, link) => {
    setSlides((prev) => {
      const next = prev.map((s, idx) => (idx === i ? { ...s, link } : s));
      notify(next);
      return next;
    });
  };

  const removeSlide = (i) => {
    setSlides((prev) => {
      const next = prev.filter((_, idx) => idx !== i);
      notify(next);
      return next;
    });
  };

  const moveSlide = (i, dir) => {
    setSlides((prev) => {
      const next = [...prev];
      const target = i + dir;
      if (target < 0 || target >= next.length) return next;
      [next[i], next[target]] = [next[target], next[i]];
      notify(next);
      return next;
    });
  };

  return (
    <div>
      <p className="text-muted small mb-3">
        Add one or more images. Customers will see them rotate as a slider, in the order shown below.
      </p>

      {slides.map((slide, i) => (
        <div
          key={i}
          className="d-flex align-items-center gap-2 mb-3 pb-3"
          style={{ borderBottom: "1px solid #e9ecef" }}
        >
          <img
            src={slide.image}
            alt={`Slide ${i + 1}`}
            style={{
              width: 220,
              height: 110,
              objectFit: "cover",
              borderRadius: 8,
              flexShrink: 0,
              border: "1px solid #dee2e6",
            }}
          />
          <Form.Control
            type="text"
            placeholder="https://example.com (optional link)"
            value={slide.link || ""}
            onChange={(e) => updateLink(i, e.target.value)}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => moveSlide(i, -1)}
            disabled={i === 0}
            title="Move up"
          >
            ↑
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => moveSlide(i, 1)}
            disabled={i === slides.length - 1}
            title="Move down"
          >
            ↓
          </button>
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={() => removeSlide(i)}
            title="Remove slide"
          >
            <i className="bi bi-trash"></i>
          </button>
        </div>
      ))}

      <Form.Group className="mt-2">
        <Form.Label className="fw-semibold">Add Slide Image</Form.Label>
        {uploading ? (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <span className="text-muted small">Uploading...</span>
          </div>
        ) : (
          <Form.Control
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
          />
        )}
        <Form.Text className="text-muted">
          Slides rotate automatically every 4 seconds.
        </Form.Text>
      </Form.Group>
    </div>
  );
};

export default BannerSlideManager;
